import { Component, ElementRef, OnInit, OnDestroy, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ICapabilityRequest, ICapabilityRequestAttachment } from '../interfaces/ICapabilityRequest';
import { FormService } from '../services/form/form.service';
import { BlobUploadService } from '../services/blob-upload/blob-upload.service';
import { LoaderService } from '../services/loader/loader.service';
import { take, takeUntil, filter, tap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-capability-request',
  templateUrl: './capability-request.component.html',
  styleUrls: ['./capability-request.component.scss']
})
export class CapabilityRequestComponent implements OnInit, OnDestroy {
  /**
   * QueryList of all FormGroup objects in component so that they can be easily iterated through
   */
  @ViewChildren(FormGroup) formGroups: QueryList<FormGroup>;
  /**
   * Form attachments input
   */
  @ViewChild('attachmentElementRef') attachmentElementRef: ElementRef;

  /**
   * Part Information FormGroup
   */
  public part_information: FormGroup = new FormGroup({
    oem: new FormControl(),
    partno: new FormControl(),
    customerpartno: new FormControl(),
    machineoem: new FormControl(),
    function: new FormControl(),
    quantity: new FormControl(0, [Validators.min(1), Validators.max(999999999)]),
    mdspartno: new FormControl(null, [Validators.min(1), Validators.max(999999999999999999)]),
    productoptions: new FormControl([], [Validators.minLength(1)])
  });

  /**
   * Repair Information FormGroup
   */
  public repair_information: FormGroup = new FormGroup({
    type: new FormControl(),
    description: new FormControl()
  });

  /**
   * Requestor Information FormGroup
   */
  public requestor_information: FormGroup = new FormGroup({
      company: new FormControl(),
      firstname: new FormControl(),
      lastname: new FormControl(),
      line1: new FormControl(),
      city: new FormControl(),
      state: new FormControl(),
      zip: new FormControl(),
      email: new FormControl(),
      phone: new FormControl(),
      fax: new FormControl(),
      preference: new FormControl('email', [])
  });

  /**
   * Additional Information FormGroup
   */
  public additional_information: FormGroup = new FormGroup({
    quoteneededby: new FormControl(),
    requireddeldate: new FormControl(),
    hotorder: new FormControl(),
    comments: new FormControl()
  });

  /**
   * Possible repair type values for dropdown
   */
  public repair_types: string[] = new Array<string>(
    'Ball Screws',
    'Electro-Hydraulics/Pneumatics',
    'General Electronics',
    'Industrial Controls',
    'Industrial Terminals & Monitors',
    'Drives & Starters',
    'Spindles',
    'Servo Motors',
    'Encoders/Resolvers',
    'Actuators/Positioners',
    'Gearboxes',
    'Pumps',
    'General Mechanical',
    'Unknown'
  );

  /**
   * Array of state abbreviations for dropdown list
   */
  public states: string[] = new Array<string>(
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  );

  /**
   * Array of product options for dropdown list
   */
  public productOptions: string[] = new Array<string>(
    'New', 'Repair', 'Surplus/Refurbished'
  );

  /**
   * Array of allowed file extensions for attachment field
   */
  public allowedFileExts: string[] = new Array<string>('.jpg', '.jpeg', '.png', '.pdf');

  /**
   * Unsubscribe on destroy
   */
  private _unsubscribe: Subject<void> = new Subject();

  constructor(private _snackbar: MatSnackBar, private _loader: LoaderService, private _blob: BlobUploadService, private _form: FormService, private _router: Router) { }

  ngOnInit(): void {
    // Subscribe to changes on fax and phone controls so we can determine if preference value needs to get reset when control is disabled
    this.requestor_information.get('fax')?.valueChanges
    .pipe(takeUntil(this._unsubscribe), filter(v => !v), tap((v) => this.resetPreferenceControl('fax'))).subscribe();

    this.requestor_information.get('phone')?.valueChanges
    .pipe(takeUntil(this._unsubscribe), filter(v => !v), tap((v) => this.resetPreferenceControl('phone'))).subscribe();
  }

  /**
   * Reset preference control if name of control/value being cleared is the current value of preference
   * @param formControlName Name of control/value that is being cleared
   */
  private resetPreferenceControl(formControlName: string){
    const prefControl = this.requestor_information.get('preference');

    if (prefControl?.value === formControlName){
      prefControl.setValue('email');
    }
  }

  /**
   * Submit form - upload attachments, send request to function app
   */
  async formSubmit(): Promise<void> {
    this._loader.shown = true;

    // Clear repair information before posting form data if 'Repair' product option isn't selected
    const productOptions = this.part_information.get('productoptions')?.value || [];
    if (productOptions.indexOf('Repair') < 0){
      this.repair_information.reset();
    }

    this.formGroups.map((g) => g.disable());

    const fileList = this.getAttachments();
    let attachments: ICapabilityRequestAttachment[] = [];

    if (fileList.length > 0){
      try{
        this._snackbar.open('Uploading Files', 'Dismiss', {duration: 5000, horizontalPosition: 'end', verticalPosition: 'top'});
        attachments = await this._blob.uploadBlob(fileList);
      }catch (e){
        console.error(e);
        this._snackbar.open('Error Uploading Files', 'Dismiss', {duration: 5000, horizontalPosition: 'end', verticalPosition: 'top'});
        this._loader.shown = false;
        this.formGroups.map((g) => g.enable());
        return;
      }
    }

    this._form.post(this._mapFormToInterface(attachments)).pipe(take(1)).subscribe((result) => {
      this._loader.shown = false;
      this._router.navigate(['confirmation', result.data.requestId]);

    }, (err: HttpErrorResponse) => {
      if (err.status == 400){
        this._snackbar.open(`Error submitting form: ${err.error.data.message}`, 'Dismiss', {duration: 5000, horizontalPosition: 'end', verticalPosition: 'top'});
      }else{
        this._snackbar.open(`Error submitting form: ${err.status} - ${err.statusText}`, 'Dismiss', {duration: 5000, horizontalPosition: 'end', verticalPosition: 'top'});
      }

      this._loader.shown = false;
      this.formGroups.map((g) => g.enable());
    });

  }

  /**
   * Get form attachments; only public for testing reasons, don't use outside component
   * @returns {File[]} FileList containing attachments on form
   */
  public getAttachments(): File[] {
    if (this.attachmentElementRef){
      return Array.from(this.attachmentElementRef.nativeElement.files);
    }else{
      return [];
    }
  }

  public validateAttachments(fileArray: File[]): boolean {
    const filesAllowed: boolean = fileArray.every((f) => {
      const ext = this._blob.getFileExtension(f.name);
      return this.allowedFileExts.indexOf(ext || '.invalid') > -1;
    });
    if (filesAllowed){
      return true;
    } else {
      this._snackbar.open(`File(s) do not have valid extensions.  Files must have allowed file extensions: ${this.allowedFileExts.join(',')}`, 'Dismiss', {duration: 5000, horizontalPosition: 'end', verticalPosition: 'top'});
      this.attachmentElementRef.nativeElement.value = null;
      return false;
    }

  }

  /**
   * Map form fields to ICapabilityRequest object
   * @param {ICapabilityRequestAttachment[]} attachments Optional attachments to include in return object
   * @returns {ICapabilityRequest} Complete ICapabilityRequest object
   */
  private _mapFormToInterface(attachments?: ICapabilityRequestAttachment[]): ICapabilityRequest{
    return {
      part: {
        oem: this.part_information.get('oem')?.value || undefined,
        partno: this.part_information.get('partno')?.value || undefined,
        customerpartno: this.part_information.get('customerpartno')?.value || undefined,
        machineoem: this.part_information.get('machineoem')?.value || undefined,
        function: this.part_information.get('function')?.value || undefined,
        quantity: this.part_information.get('quantity')?.value || undefined,
        mdspartno: this.part_information.get('mdspartno')?.value || undefined,
        productoptions: this.part_information.get('productoptions')?.value || undefined,
        attachments
      },
      requestor: {
        contact: {
          email: this.requestor_information.get('email')?.value || undefined,
          preference: this.requestor_information.get('preference')?.value || undefined,
          phone: this.requestor_information.get('phone')?.value || undefined,
          fax: this.requestor_information.get('fax')?.value || undefined
        },
        firstname: this.requestor_information.get('firstname')?.value || undefined,
        lastname: this.requestor_information.get('lastname')?.value || undefined,
        company: this.requestor_information.get('company')?.value || undefined,
        address: {
          line1: this.requestor_information.get('line1')?.value || undefined,
          city: this.requestor_information.get('city')?.value || undefined,
          state: this.requestor_information.get('state')?.value || undefined,
          zip: this.requestor_information.get('zip')?.value || undefined
        }
      },
      repair: {
        type: this.repair_information.get('type')?.value || undefined,
        description: this.repair_information.get('description')?.value || undefined,
      },
      additional: {
        quoteneededby: this.additional_information.get('quoteneededby')?.value || undefined,
        requireddeldate: this.additional_information.get('requireddeldate')?.value || undefined,
        hotorder: this.additional_information.get('hotorder')?.value || false,
        comments: this.additional_information.get('comments')?.value || undefined
      }
    };
  }

  ngOnDestroy(): void {
      this._unsubscribe.next();
      this._unsubscribe.complete();
  }
}
